import { useState } from 'react';
import { Loader2, StickyNote } from 'lucide-react';
import moment from 'moment';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useAddClaimNoteMutation } from '@/client/api/ownership-claims/ownership-claims.mutation';
import type { ClaimNote } from '@/client/types/ownership-claims';
import { PermissionGate } from '@/client/components/admin/PermissionButton';
import { personLabel } from './claimStatus';

/** Admin-only notes — never shown to the claimant or listing owner. */
const ClaimNotes = ({ claimid, notes }: { claimid: number; notes: ClaimNote[] }) => {
  const [draft, setDraft] = useState('');
  const mutation = useAddClaimNoteMutation();

  const add = async () => {
    const note = draft.trim();
    if (!note) return;
    try {
      await mutation.mutateAsync({ claimid, note });
      setDraft('');
    } catch (error: any) {
      toast.error('Could not add note', { description: error?.message, richColors: true });
    }
  };

  return (
    <div>
      <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-gray-900">
        <StickyNote className="h-4 w-4 text-gray-400" /> Internal notes ({notes.length})
      </p>
      {notes.length > 0 && (
        <ul className="mb-3 space-y-2">
          {notes.map((n) => (
            <li key={n.cnid} className="rounded-lg bg-yellow-50 p-3 text-sm text-gray-800">
              <p className="whitespace-pre-wrap">{n.note}</p>
              <p className="mt-1 text-xs text-gray-500">
                {personLabel(n.author)} · {n.date_added ? moment(n.date_added).format('YYYY-MM-DD HH:mm') : ''}
              </p>
            </li>
          ))}
        </ul>
      )}
      <PermissionGate code="claim.view">
        <div className="flex gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            maxLength={2000}
            placeholder="Add a note for other admins"
            className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
          <Button size="sm" onClick={() => void add()} disabled={!draft.trim() || mutation.isPending}>
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add'}
          </Button>
        </div>
      </PermissionGate>
    </div>
  );
};

export default ClaimNotes;
